import { useEffect, useState } from "react";
import FullScreenModal from "@/components/FullScreenModal";
import { formatCurrency } from "@vaulzo/core/constants";
import { EXPENSE_CATEGORIES, groupsForDate, todayISO } from "@vaulzo/core/domain/expenses";
import { kicker, fieldLabel, underline, PrimaryButton, SoftButton } from "./ui";

const NEW_GROUP = "__new__";

/**
 * Add or edit one expense. The group list follows the date: only groups
 * counted in that month are offered, plus "New group…". `preset` seeds
 * a new entry (date, category, group) from wherever it was opened.
 */
export default function ExpenseDialog({ open, onClose, onSubmit, data, editing = null, preset = {}, isSubmitting = false, onDelete = null }) {
  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState("food");
  const [group, setGroup] = useState("");
  const [newGroup, setNewGroup] = useState("");

  useEffect(() => {
    if (!open) return;
    setDate(editing?.date || preset.date || todayISO());
    setCategory(editing?.category || preset.category || "food");
    setGroup(editing?.group_id || preset.groupId || "");
    setNewGroup("");
  }, [open, editing, preset]);

  const groups = groupsForDate(data, date);
  // Keep a group picked for another month (an entry being moved) in the list.
  const current = group && group !== NEW_GROUP && !groups.some((g) => g.id === group) ? (data?.groups || []).find((g) => g.id === group) : null;
  const options = current ? [current, ...groups] : groups;

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      date,
      name: fd.get("name"),
      amount: parseFloat(fd.get("amount")),
      category,
      note: fd.get("note") || "",
      group_id: group && group !== NEW_GROUP ? group : "",
      new_group: group === NEW_GROUP ? newGroup.trim() : "",
    };
    if (group === NEW_GROUP && !payload.new_group) return;
    onSubmit(payload);
  };

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="w-full max-w-[560px]">
        <div className={kicker}>Expense</div>
        <h2 className="m-0 mb-11 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">{editing ? "Edit expense" : "Add expense"}</h2>

        <div className="flex flex-col gap-[30px]">
          <label className="block">
            <span className={fieldLabel}>Amount</span>
            <div className="flex items-baseline gap-[10px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-accent">
              <span className="text-[24px] text-vaulzo-fainter">₹</span>
              <input
                name="amount"
                type="number"
                step="0.01"
                min="0.01"
                required
                inputMode="decimal"
                autoFocus={!editing}
                defaultValue={editing?.amount}
                placeholder="0"
                className="num min-w-0 flex-1 border-0 bg-transparent py-2 text-[24px] font-semibold text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
              />
            </div>
          </label>

          <label className="block">
            <span className={fieldLabel}>What was it?</span>
            <input name="name" required maxLength={120} defaultValue={editing?.name} placeholder="e.g. Biryani by Kilo" className={`${underline} text-base`} />
          </label>

          <div className="flex flex-col gap-6 sm:flex-row">
            <label className="block flex-1">
              <span className={fieldLabel}>Date</span>
              <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={`num ${underline} text-[15px]`} />
            </label>
            <label className="block flex-1">
              <span className={fieldLabel}>Part of a group?</span>
              <select value={group} onChange={(e) => setGroup(e.target.value)} className={`${underline} cursor-pointer appearance-none text-[15px]`}>
                <option value="">No, a normal expense</option>
                {options.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
                <option value={NEW_GROUP}>New group…</option>
              </select>
              {group === NEW_GROUP && (
                <input
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  required
                  maxLength={80}
                  placeholder="Group name, e.g. Goa trip, PC upgrade"
                  className={`${underline} mt-3 text-[15px]`}
                />
              )}
            </label>
          </div>

          <div className="block">
            <span className={fieldLabel}>Category</span>
            <div className="flex flex-wrap gap-2">
              {EXPENSE_CATEGORIES.map((c) => {
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`rounded-full border-[1.5px] px-[13px] py-[7px] text-[13px] transition-colors ${
                      active ? "border-vaulzo-accent bg-vaulzo-accent font-semibold text-vaulzo-on-fill" : "border-vaulzo-chip bg-transparent font-medium text-vaulzo-muted hover:border-vaulzo-accent"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className={fieldLabel}>Note (optional)</span>
            <input name="note" maxLength={500} defaultValue={editing?.note} placeholder="e.g. paid with Amex, split with Deepak" className={`${underline} text-[15px]`} />
          </label>
        </div>

        <div className="mt-[52px] flex items-center gap-3">
          <SoftButton onClick={onClose} disabled={isSubmitting}>Cancel</SoftButton>
          <PrimaryButton busy={isSubmitting}>{editing ? "Save changes" : "Add expense"}</PrimaryButton>
          {editing && onDelete && (
            <>
              <span className="flex-1" />
              <button type="button" onClick={onDelete} className="py-2 text-[14px] font-semibold text-vaulzo-danger transition-opacity hover:opacity-60">
                Delete
              </button>
            </>
          )}
        </div>
        {editing?.recurring_id && (
          <p className="mt-4 text-[12px] text-vaulzo-fainter">This entry came from a recurring expense. Changing it here changes only this month; the {formatCurrency(editing.amount)} rule stays as it is.</p>
        )}
      </form>
    </FullScreenModal>
  );
}
